import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { GENERAL_ERROR_MESSAGE } from '@shared/models/messages';

/**
 * Presentational component for displaying a backend validation error
 * (e.g. `AssessmentToolValidationException`) that carries a high-level
 * summary plus an optional list of detail messages.
 *
 * Each detail is rendered on its own line, prefixed with a "•" bullet.
 *
 * Usage:
 * ```html
 * <app-validation-error
 *   [error]="uploadError()"
 *   summaryPrefix="Failed to create Bordereau Assessment. "
 *   testId="treaty-upload-error"
 * />
 * ```
 */
@Component({
  selector: 'app-validation-error',
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (resolvedSummary()) {
      <div class="dup-check dup-check--error" [attr.data-testid]="testId()" role="alert" aria-live="assertive">
        <mat-icon>error</mat-icon>
        <div class="dup-check__body">
          <span [innerHTML]="resolvedSummary()"></span>
          @if (resolvedDetails().length) {
            <div class="dup-check__details">
              @for (detail of resolvedDetails(); track detail) {
                <span class="dup-check__detail"> • <span [innerHTML]="detail"></span> </span>
              }
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: `
    .dup-check {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.625rem 0.875rem;
      margin-bottom: 1rem;
      border-radius: 4px;
      font-size: 0.85rem;
      line-height: 1.35;
    }

    .dup-check mat-icon {
      flex-shrink: 0;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .dup-check__body {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .dup-check__details {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
    }

    .dup-check__detail {
      display: block;
      white-space: pre-line;
    }

    .dup-check--error {
      color: var(--error-color);
      background: var(--mandatory-red-background);
    }

    .dup-check--error mat-icon {
      color: var(--error-color);
    }
  `,
})
export class ValidationErrorComponent {
  private static readonly DEFAULT_ERROR_MESSAGE = GENERAL_ERROR_MESSAGE;
  private readonly sanitizer = inject(DomSanitizer);

  /** Raw backend/http error object that carries message and details. */
  readonly error = input<unknown | null>(null);
  /** Optional prefix prepended to the resolved summary. */
  readonly summaryPrefix = input('');
  /** Optional test ID for the error container. */
  readonly testId = input<string>('');

  readonly resolvedSummary = computed<SafeHtml | null>(() => {
    const err = this.error();

    if (!err) {
      return null;
    }

    return this.toSafeHtml(`${this.summaryPrefix()}${this.getErrorMessage(err)}`);
  });

  readonly resolvedDetails = computed<SafeHtml[]>(() => {
    const err = this.error();

    if (!err) {
      return [];
    }

    return this.getErrorDetails(err).map(detail => this.toSafeHtml(detail));
  });

  private getErrorMessage(error: unknown): string {
    const message = (error as { error?: { message?: unknown } })?.error?.message;

    if (typeof message !== 'string') {
      return ValidationErrorComponent.DEFAULT_ERROR_MESSAGE;
    }

    return message.trim() || ValidationErrorComponent.DEFAULT_ERROR_MESSAGE;
  }

  private toSafeHtml(value: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value);
  }

  private getErrorDetails(error: unknown): string[] {
    const details = (error as { error?: { details?: unknown[] } })?.error?.details;

    if (!Array.isArray(details)) {
      return [];
    }

    return details.filter((detail: unknown): detail is string => typeof detail === 'string' && !!detail.trim());
  }
}
